<template>
    <v-dialog v-if="dialog" v-model="dialog" fullscreen persistent @keydown.esc="cancel" @click:outside="cancel">
        <v-card class="text-body-2 fill-height" tile>
            <v-toolbar dark flat color="#2B3848" class="flex-grow-0">
                <v-toolbar-title class="text-body-1 font-weight-bold white--text">Centro de Control</v-toolbar-title>
                <v-text-field
                    v-model="query"
                    flat
                    solo-inverted
                    append-icon="mdi-magnify"
                    label="Buscar"
                    single-line
                    hide-details
                    clearable
                    @keydown.enter="search"
                    @click:clear="clear"
                    class="mx-4"
                    ref="command-center-search"
                />
                <v-btn color="error" @click="cancel">
                    <v-icon>mdi-close</v-icon>
                    Cerrar
                </v-btn>
            </v-toolbar>

            <div v-if="searching" class="flex-grow-1">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
            </div>

            <div v-else class="d-flex" style="height: calc(100% - 64px)">
                <div class="d-flex flex-column scrollbox2 fill-height leftpanel" :class="somethingSelected ? 'collapsed' : ''">
                    <v-list>
                        <v-list-item-group v-for="group in results" :key="group.name" :value="selectedId" color="primary">
                            <v-subheader class="text-subtitle-1 font-weight-bold">{{ group.name }}</v-subheader>
                            <v-divider />

                            <v-list-item v-for="item in group.items" @click="selectItem(group.type, item.id)" :key="item.id" :value="item.id" class="px-4 d-flex">
                                <v-list-item-avatar v-if="item.icon || item.image" class="flex-justify-center">
                                    <img v-if="item.image" :src="`/uploads/${item.image}/64`" width="32" height="32" class="" />
                                    <v-icon v-else :color="item.iconColor">{{ item.icon }}</v-icon>
                                </v-list-item-avatar>

                                <v-list-item-content v-if="group.type === 'BOOKINGS'">
                                    <v-list-item-title>
                                        <span class="font-weight-bold">{{ item.reference }}</span>
                                        <span class="ml-2 grey--text">{{ formatDate(item.date) }}</span>
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        {{ item.title }}
                                    </v-list-item-subtitle>
                                    <v-list-item-subtitle>
                                        {{ item.subtitle }}
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-content v-else>
                                    <v-list-item-title>
                                        {{ item.title }}
                                    </v-list-item-title>
                                    <v-list-item-subtitle v-if="item.subtitle">
                                        {{ item.subtitle }}
                                    </v-list-item-subtitle>
                                </v-list-item-content>

                                <v-list-item-action v-if="group.type === 'BOOKINGS'">
                                    <span v-if="item.status === 'CANCELLED'" class="red white--text rounded px-2 py-1 font-weight-medium ml-2">CANCELADA</span>
                                    <span v-else-if="item.status === 'CONFIRMED'" class="green white--text rounded px-2 py-1 font-weight-medium ml-2">CONFIRMADA</span>
                                    <span v-else-if="item.status === 'NOSHOW'" class="orange white--text rounded px-2 py-1 font-weight-medium ml-2">NO SHOW</span>
                                    <span v-else-if="item.status === 'PENDING'" class="orange white--text rounded px-2 py-1 font-weight-medium ml-2">PENDIENTE</span>
                                </v-list-item-action>
                                <v-list-item-action v-if="group.type === 'GUIDES' || group.type === 'AGENCIES'">
                                    <span v-if="item.phone" class="blue darken-2 white--text rounded px-2 py-1 font-weight-medium ml-2">
                                        <v-icon color="white" class="mr-1">mdi-phone</v-icon>
                                        {{ item.phone }}
                                    </span>
                                </v-list-item-action>
                            </v-list-item>
                        </v-list-item-group>
                    </v-list>
                </div>

                <CommandCenterBookingView v-if="somethingSelected && selectedType === 'BOOKINGS'" :itemId="selectedId" class="rightpanel" @open="openBooking" />
                <CommandCenterGuideView v-if="somethingSelected && selectedType === 'GUIDES'" :itemId="selectedId" class="rightpanel" @open="openGuide" />
            </div>
        </v-card>
    </v-dialog>
</template>

<style lang="scss">
.leftpanel {
    width: 100%;
    height: 100%;

    &.collapsed {
        width: 400px;
    }
}

.rightpanel {
    width: calc(100% - 400px);
}
</style>

<script lang="ts">
import { EventBus } from "@/components/EventBus";
import { OpenCommandCenterRequest, DataClient } from "@/services/DataClient";
import { SearchCommandCenterResponse } from "@/services/ClientInterfaces";
import { Utilities } from "@/services/Utilities";
import { Component, Vue, Watch } from "vue-property-decorator";
import { BaseView } from "@/components/BaseView";
import CommandCenterBookingView from "./Components/CommandCenterBookingView.vue";
import CommandCenterGuideView from "./Components/CommandCenterGuideView.vue";

@Component({
    components: {
        CommandCenterBookingView,
        CommandCenterGuideView,
    },
})
export default class extends BaseView {
    dialog = false;

    query = "";
    searching = false;

    selectedType = "";
    selectedId = "";

    results: SearchCommandCenterResponse = [];

    mounted() {
        EventBus.$on(DataClient.CommandCenter.OpenCommandCenterDialogEvent, this.handleOpenEvent);
    }

    beforeDestroy() {
        EventBus.$off(DataClient.CommandCenter.OpenCommandCenterDialogEvent, this.handleOpenEvent);
    }

    get somethingSelected() {
        return this.selectedType && this.selectedId;
    }

    async handleOpenEvent(event: OpenCommandCenterRequest) {
        if (this.dialog) {
            this.dialog = false;
            return;
        }

        this.query = "";
        this.results = [];

        this.search();

        this.dialog = true;

        this.focusSearchBar();
    }

    accept() {
        this.dialog = false;

        DataClient.CommandCenter.open(true, true);
    }

    focusSearchBar() {
        Utilities.focus(this, "command-center-search");
    }

    cancel() {
        this.dialog = false;

        DataClient.CommandCenter.open(false, true);
    }

    async search() {
        this.searching = true;

        this.selectedType = "";
        this.selectedId = "";

        try {
            this.results = await DataClient.CommandCenter.search({ query: this.query });

            if (this.results.length === 1 && this.results[0].items.length === 1) {
                this.selectItem(this.results[0].type, this.results[0].items[0].id);
            }
        } catch (error) {
            this.openErrorDialog("Error durante la búsqueda", error);
        } finally {
            this.searching = false;
        }
    }

    clear() {
        this.query = "";
        this.search();
    }

    formatDate(date?: string) {
        return Utilities.formatDateToDDMMYYYY(date || "");
    }

    async selectItem(type: string, id: string) {
        this.selectedType = type;
        this.selectedId = id;

        this.focusSearchBar();
    }

    openBooking(id: string) {
        this.cancel();
        this.$router.push({ name: "ViewBooking", params: { id } });
    }

    openGuide(id: string) {
        this.cancel();
        this.$router.push({ name: "ViewGuide", params: { id } });
    }
}
</script>
